package com.essntl.features.dashboard.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.unit.dp
import com.essntl.features.dashboard.ui.DashboardEvent
import com.essntl.features.dashboard.ui.DashboardNavigation
import com.essntl.features.dashboard.ui.DashboardState
import com.essntl.features.dashboard.ui.components.itemcard.ItemProposalCard
import com.essntl.features.dashboard.ui.rememberFilteredProposals
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.toggle.EsTab
import com.essntl.uikit.components.toggle.TabState
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.launch

@OptIn(FlowPreview::class)
@Composable
fun ColumnScope.DashboardItems(
    state: DashboardState,
    onNavigation: (DashboardNavigation) -> Unit,
    onEvent: (DashboardEvent) -> Unit,
    selectedId: String?,
) {
    val filteredProposals = state.rememberFilteredProposals()

    val isLoading = remember(state) { state.itemsLoading }
    val isFailure = remember(state) { state.itemsFailure }
    val isEmpty = remember(state) { state.itemsEmpty }

    val tabItems = listOf(
        TabState(index = 0, text = "Assigned"),
        TabState(index = 1, text = "Last opened"),
    )

    val selectedTabIndex = remember(state.assigned) {
        if (state.assigned) 0 else 1
    }

    var scrollPosition by remember {
        mutableStateOf(0)
    }

    val lazyListState = rememberLazyListState(
        initialFirstVisibleItemIndex = scrollPosition,
    )

    LaunchedEffect(lazyListState) {
        snapshotFlow {
            lazyListState.firstVisibleItemIndex
        }.debounce(500L)
            .collectLatest { index ->
                scrollPosition = index
            }
    }

    val scope = rememberCoroutineScope()

    Column(
        modifier = Modifier
            .padding(top = 1.spacing)
            .clip(RoundedCornerShape(EsConstants.SectionBorderRadius))
            .background(MaterialTheme.esColorScheme.bgSecondary)
            .padding(
                vertical = EsConstants.SectionVerticalPadding,
                horizontal = EsConstants.SectionHorizontalPadding,
            )
            .fillMaxWidth(),
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center,
        ) {
            Text(
                text = "Proposals",
                color = MaterialTheme.esColorScheme.textPrimary,
                style = MaterialTheme.esTypography.h4SemiBold20,
                modifier = Modifier.padding(vertical = 1.spacing),
            )

            Spacer(modifier = Modifier.weight(1f))

            EsTab(
                items = tabItems,
                selectedItemIndex = selectedTabIndex,
                onClick = { tabState ->
                    if (tabState.index == selectedTabIndex) return@EsTab
                    onEvent(DashboardEvent.SwitchProposals)
                },
            )
        }

        Spacer(modifier = Modifier.height(1.5.spacing))

        if (isLoading) {
            EsSpinner(
                modifier = Modifier.align(Alignment.CenterHorizontally),
            )
        } else if (isFailure) {
            Text(
                text = "Something goes wrong!",
                color = MaterialTheme.esColorScheme.textSecondary,
                style = MaterialTheme.esTypography.p1Medium16,
            )
        } else if (isEmpty) {
            Text(
                text = "Nothing was found!",
                color = MaterialTheme.esColorScheme.textSecondary,
                style = MaterialTheme.esTypography.p1Medium16,
            )
        } else
            Box(
                contentAlignment = Alignment.Center,
            ) {
                LazyRow(
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                    state = lazyListState,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    items(
                        if (state.assigned)
                            filteredProposals else
                            state.getAllProposal.data.orEmpty(),
                    ) { proposal ->
                        key(proposal.id) {
                            ItemProposalCard(
                                proposal = proposal,
                                selectedId = selectedId,
                                onClick = {
                                    onNavigation(DashboardNavigation.EditProposal(proposal.id))
                                },
                            )
                        }
                    }

//                state.getAllRequest.data.orEmpty().forEach { request ->
//                    key(request.id) {
//                        ItemRequestCard(
//                            id = request.id,
//                            title = request.title ?: "Request",
//                            iconRes = EsIcons.Request,
//                            timestamp = request.createdAt,
//                            selectedId = selectedId,
//                            onClick = {
//                                onNavigation(DashboardNavigation.EditRequest(request.id))
//                            },
//                        )
//                    }
//                }
                }

                if (lazyListState.canScrollBackward ||
                    lazyListState.canScrollForward
                )
                    Box(modifier = Modifier.fillMaxWidth()) {
                        if (lazyListState.canScrollBackward)
                            EsLargeIcon(
                                res = EsIcons.ArrowLeft,
                                tint = MaterialTheme.esColorScheme.iconError,
                                modifier = Modifier
                                    .clip(CircleShape)
                                    .pointerHoverIcon(PointerIcon.Hand)
                                    .clickable {
                                        scope.launch {
                                            lazyListState.animateScrollToItem(scrollPosition - 1)
                                        }
                                    }
                                    .padding(.5.spacing)
                                    .align(Alignment.CenterStart),
                            )

                        if (lazyListState.canScrollForward)
                            EsLargeIcon(
                                res = EsIcons.ArrowRight,
                                tint = MaterialTheme.esColorScheme.iconError,
                                modifier = Modifier
                                    .clip(CircleShape)
                                    .pointerHoverIcon(PointerIcon.Hand)
                                    .clickable {
                                        scope.launch {
                                            lazyListState.animateScrollToItem(scrollPosition + 1)
                                        }
                                    }
                                    .padding(.5.spacing)
                                    .align(Alignment.CenterEnd),
                            )
                    }
            }
    }
}